import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthorizationData } from '@zero/shared/util-models';
import { AuthCodeBaseData, PreAuthorizationSessionAuth } from '@zero/shared/util-web-app-services';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { Credentials } from '../models/credentials';
import { OAuthParameters } from '../models/oauth-parameters';
import { PublicKeyData } from '../models/public-key-data';
import { ServerAuthcodeData } from '../models/server-authcode-data';
import { RSA } from './rsa';

@Injectable({
  providedIn: 'root',
})
export class OauthService {
  constructor(private http: HttpClient) {}

  public async initialize() {
    await this.getPublicKeyData();
  }

  /***
   * Authenticate the user against SAM-OAuth backend
   * @param username Username
   * @param password Password
   * @param params OAuth 2 params passing to the backend
   */
  public async authenticate(username: string, password: string, params: OAuthParameters): Promise<AuthorizationData<AuthCodeBaseData>> {
    const credentials = await this.encryptCredentials(username, password);

    const requestUrl = `${environment.serverlessApihost}${environment.oAuth2Endpoints.authenticate}?response_type=code&client_id=${params.clientId}&redirect_uri=${params.redirectUri}&scope=${params.scope}&state=${params.state}`;
    const response = await firstValueFrom(this.http.post<any>(requestUrl, { c: credentials }));

    if (response.type === 'USER') {
      return new AuthorizationData<PreAuthorizationSessionAuth>(
        new PreAuthorizationSessionAuth(
          response.type,
          response.hash,
          response.customerId,
          response.username,
          response.verifiedPhoneNumber,
          response.enrolledTANDevices,
        ),
      );
    }

    // Server user response
    return new AuthorizationData<ServerAuthcodeData>(new ServerAuthcodeData(response.type, response.authCode));
  }

  /***
   * Receives the DER formatted public key
   * @private
   */
  private getPublicKeyData(): Promise<PublicKeyData> {
    return firstValueFrom(this.http.get<PublicKeyData>(`${environment.serverlessApihost}${environment.oAuth2Endpoints.publicKey}`));
  }

  /***
   * Encrypts user credentials with servers' public key
   * @param username Username
   * @param password Password
   */
  private async encryptCredentials(username: string, password: string) {
    const publicKeyData = await this.getPublicKeyData();
    const rsaKey = await RSA.createKeyFromBuffer(Buffer.from(publicKeyData.key, 'base64'));

    return await RSA.encryptMessage(rsaKey, JSON.stringify(new Credentials(username, password, publicKeyData.nonce)));
  }
}
